import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";

import { Button } from "../components/ui";
import {
  ErrorState,
  InlineNotice,
  Loading,
  PageHeader,
  Panel,
} from "../components/WorkspacePrimitives";
import { startArtifactDownload } from "../lib/artifactDownload";
import {
  type CliReleaseTarget,
  useCliReleaseDownload,
  useLatestCliRelease,
} from "./cliDownloads";
import SettingsLayout from "./SettingsLayout";
import "./settings.css";

const TARGET_LABELS: Record<CliReleaseTarget, string> = {
  "darwin-arm64": "macOS — Apple silicon",
  "darwin-x86_64": "macOS — Intel",
  "linux-x86_64": "Linux — x86-64",
  "windows-x86_64": "Windows — x86-64",
};

const TARGET_ORDER = [
  "darwin-arm64",
  "darwin-x86_64",
  "linux-x86_64",
  "windows-x86_64",
] as const satisfies readonly CliReleaseTarget[];

export function recommendedCliTarget({
  platform,
  userAgent,
}: {
  platform: string;
  userAgent: string;
}): CliReleaseTarget | null {
  const identity = `${platform} ${userAgent}`.toLowerCase();
  if (identity.includes("windows") || identity.includes("win32")) {
    return "windows-x86_64";
  }
  if (identity.includes("linux") && !identity.includes("android")) {
    return "linux-x86_64";
  }
  if (
    identity.includes("mac") &&
    /(arm64|aarch64|apple silicon)/.test(identity)
  ) {
    return "darwin-arm64";
  }
  return null;
}

function currentRecommendation(): CliReleaseTarget | null {
  if (typeof navigator === "undefined") return null;
  return recommendedCliTarget({
    platform: navigator.platform,
    userAgent: navigator.userAgent,
  });
}

export default function CliSettingsPage() {
  const release = useLatestCliRelease();
  const download = useCliReleaseDownload();
  const recommended = currentRecommendation();

  const startDownload = async (version: string, target: CliReleaseTarget) => {
    try {
      const result = await download.mutateAsync({ version, target });
      startArtifactDownload(result.url);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "CLI download failed.",
      );
    }
  };

  const artifacts = release.data
    ? [...release.data.artifacts].sort(
        (left, right) =>
          TARGET_ORDER.indexOf(left.target) -
          TARGET_ORDER.indexOf(right.target),
      )
    : [];

  return (
    <SettingsLayout>
      <div className="page page--workspace cli-settings-page">
        <PageHeader className="settings-page-header">
          <h1>CLI setup</h1>
        </PageHeader>

        <Panel
          title="Download CLI"
          meta={release.data ? `Version ${release.data.version}` : undefined}
        >
          {release.isPending && !release.data ? (
            <Loading label="Loading CLI release…" />
          ) : release.error && !release.data ? (
            <ErrorState
              error={release.error}
              onRetry={() => release.refetch()}
            />
          ) : (
            <div className="cli-downloads">
              <p>
                Choose your computer. The CLI checks for newer versions once a
                day and installs them with <code>isara-driver update</code>.
              </p>
              {artifacts.some(
                (artifact) =>
                  artifact.target.startsWith("darwin-") &&
                  artifact.native_signature === "none",
              ) && (
                <InlineNotice tone="warning">
                  macOS downloads are not Apple-signed or notarized and require
                  approval under your organization&apos;s IT policy.
                </InlineNotice>
              )}
              <div className="cli-download-grid">
                {artifacts.map((artifact) => (
                  <div className="cli-download" key={artifact.target}>
                    <div>
                      <strong>{TARGET_LABELS[artifact.target]}</strong>
                      {artifact.target === recommended && (
                        <small>Recommended</small>
                      )}
                      {artifact.target.startsWith("darwin-") &&
                        artifact.native_signature === "none" && (
                          <small>Unsigned</small>
                        )}
                    </div>
                    <Button
                      tone={
                        artifact.target === recommended
                          ? "primary"
                          : "secondary"
                      }
                      disabled={download.isPending}
                      onClick={() => {
                        if (release.data) {
                          void startDownload(
                            release.data.version,
                            artifact.target,
                          );
                        }
                      }}
                    >
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Panel>

        <Panel title="Connect the CLI">
          <ol className="cli-setup-steps">
            <li>
              Open <Link to="/settings/api-tokens">API tokens</Link>, select
              <strong> Create token</strong>, and copy the token shown once.
            </li>
            <li>
              Create <code>~/.isara-driver/config.toml</code> with exactly:
              <pre>
                <code>{`url = "https://driver.isara.io/api"\napi_key = "paste-your-api-key-here"`}</code>
              </pre>
            </li>
            <li>
              On macOS or Linux, run
              <code> chmod 600 ~/.isara-driver/config.toml</code>.
            </li>
            <li>
              Verify access with <code>isara-driver whoami</code>.
            </li>
          </ol>
          <p className="cli-uninstall-note">
            To uninstall, remove the <code>isara-driver</code> executable. Keep
            the configuration for a later reinstall, or delete
            <code> ~/.isara-driver</code> to remove the saved API key too.
          </p>
        </Panel>
      </div>
    </SettingsLayout>
  );
}
